import React, { useState, useEffect } from 'react'
import { Choose } from 'react-extras'

import { Button, Icon, Row, Col, Timeline, Input, Divider, Popover, Typography, Form, Select } from 'antd'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import moment from 'moment'

import CustomSelect from '../../../../components/CustomSelect/customSelect'
import { validRule, RULES } from '../../../../constants/Entitlements'
import { api, request } from '../../../../util/handlerRequestUtil'
import { getMessage } from '../../../../util/IntlMessages'

import './actionStyles.css'

moment.updateLocale(moment.locale('pt-br'), { invalidDate: '-' })

const { Option } = Select

const STATUS = {
  PLANNED: { icon: 'planejada', color: 'blue', enum: 'PLANEJADA', label: 'PLANEJADA' },
  DONE: { icon: 'realizada', color: 'lightseagreen', enum: 'REALIZADA', label: 'REALIZADA' },
  NOT_PLANNED: { icon: 'nao_planejada', color: 'gray', enum: 'NAO_PLANEJADA', label: 'NÃO PLANEJADA' },
  LATE: { icon: 'atrasada', color: 'red', enum: 'ATRASADA', label: 'ATRASADA' }
}

const STATUS_ACTION = [
  { value: 'ALL', description: getMessage('actions.list.status.all'), class: 'filter-all' },
  { value: 'NAO_PLANEJADA', description: getMessage('actions.list.status.pending'), class: 'filter-not-planned' },
  { value: 'PLANEJADA', description: getMessage('actions.list.status.planned'), class: 'filter-planned' },
  { value: 'REALIZADA', description: getMessage('actions.list.status.finished'), class: 'filter-finished' },
  { value: 'ATRASADA', description: getMessage('actions.list.status.late'), class: 'filter-late' }
]

const STYLES = {
  statusCancelado: {
    textDecoration: 'line-through',
    color: '#bfbfbf',
    fontStyle: 'italic'
  },
  statusRealizado: {
    color: 'gray'
  },
  popover: {
    maxWidth: '150px'
  },
  editIcon: {
    fontSize: '16px',
    color: '#e55f6d'
  },
  cursorOver: {
    cursor: 'pointer'
  },
  badgeNotPlanned: {
    backgroundColor: '#f3a96d',
    padding: '5px 5px',
    display: 'inline-block',
    color: 'white',
    borderRadius: '5px',
    cursor: 'default'
  },
  badgePlanned: {
    backgroundColor: '#a8d5ff',
    padding: '5px 5px',
    display: 'inline-block',
    color: 'white',
    borderRadius: '5px',
    cursor: 'default'
  },
  badgeLate: {
    backgroundColor: '#E55F6D',
    padding: '5px 5px',
    display: 'inline-block',
    color: 'white',
    borderRadius: '5px',
    cursor: 'default'
  },
  badgeDone: {
    backgroundColor: '#83D5CC',
    padding: '5px 5px',
    display: 'inline-block',
    color: 'white',
    borderRadius: '5px',
    cursor: 'default'
  }
}

const ActionsTimeline = function ({ data = {}, onLoad, history }) {
  const [referencePeriodList, setReferencePeriodList] = useState([])
  const [searchValues, setSearchValues] = useState({ page: 0 })
  const setFormDataKey = (key, value) => setSearchValues({ ...searchValues, [key]: value })

  useEffect(() => {
    listReferencePeriod({ active: true })
    return () => {
      setReferencePeriodList([])
    }
  }, [])

  // Api requests
  async function listReferencePeriod(params) {
    try {
      const response = await request(api.listSafraDropdown(params))

      setReferencePeriodList(response)
    } catch (error) {
      console.tron.log(error)
    }
  }

  function onSearch() {
    const params = { ...searchValues, page: 0 }
    if (typeof onLoad === 'function') {
      onLoad(params)
    }
  }

  const resetFilter = () => {
    setSearchValues({ page: 0 })
    onLoad()
  }

  function onChange(setFieldValue) {
    return (field, key) => {
      if (key !== null) {
        setFormDataKey(field, key)
        onLoad({ ...searchValues, [field]: key, tab: 'timeline' })
        return setFieldValue(field, key)
      }
    }
  }

  function getStatus(value) {
    const actionStatus = value.actionStatus

    const datePlannedDone = moment(value.actionPlannedDoneTime).format('YYYY-MM-DD')
    const dateNow = moment().format('YYYY-MM-DD')
    if ((actionStatus === 'PLANEJADA' && dateNow > datePlannedDone) || actionStatus === 'ATRASADA') {
      return STATUS.LATE
    }

    if (actionStatus === STATUS.DONE.label) {
      return STATUS.DONE
    }

    if (actionStatus === STATUS.PLANNED.label) {
      return STATUS.PLANNED
    }

    return STATUS.NOT_PLANNED
  }

  function renderDot(icon) {
    if (icon !== '') {
      return (
        <img
          alt=""
          style={{ width: '22px', height: '22px' }}
          src={require('assets/images/timeline/' + icon + '.png')}
        />
      )
    }

    return null
  }

  function renderTimeline(values = {}) {
    return (
      <>
        <Typography.Title className="default-header-title" level={3} style={{ marginTop: 20, marginBottom: 30 }}>
          {getMessage('timelineActions')}
        </Typography.Title>
        <Timeline mode="alternate" pending={referencePeriodList.length === 0}>
          {renderTimelineItem(values)}
        </Timeline>
      </>
    )
  }

  function renderTimelineItem(items = {}) {
    if (isEmpty(items)) {
      return null
    }

    const list = []
    for (const [key, values] of Object.entries(items)) {
      const date_display = moment(key).locale('pt-br').utcOffset(3).format('dddd, DD [de] MMMM [de] Y')

      list.push(renderTimeItem(date_display))

      values.map((value) => {
        list.push(renderContentItem(value))
      })
    }

    return list
  }

  function renderTimeItem(key = '') {
    return (
      <Timeline.Item key={key} position="right" color="gray">
        <p style={{ color: key === '-' ? '#f3a96d' : '' }}>
          {key === '-' ? getMessage('actions.list.status.pending') : key}
        </p>
      </Timeline.Item>
    )
  }

  function renderContentItem(value = {}) {
    const status = getStatus(value)

    return (
      <Timeline.Item key={value.id} position="left" color={status.color} dot={renderDot(status.icon)}>
        <span style={STYLES.cursorOver}>{renderTooltip(value)}</span>
      </Timeline.Item>
    )
  }

  function renderInnerTooltip(value = {}) {
    const {
      id,
      customerName,
      customerTypeName,
      memberName,
      memberTypeName,
      actionStatus,
      actionTypeDescription,
      actionSubject,
      actionPlannedDoneTime,
      actionNotes,
      actionTypeId
    } = value

    return (
      <>
        <Row>
          <Col span={24}>
            <Typography.Text strong>{actionTypeDescription}</Typography.Text>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <Choose>
              <Choose.When condition={actionStatus === STATUS.PLANNED.enum}>
                <Typography.Text style={STYLES.badgePlanned}>{STATUS.PLANNED.label}</Typography.Text>
              </Choose.When>
              <Choose.When condition={actionStatus === STATUS.DONE.enum}>
                <Typography.Text style={STYLES.badgeDone}>{STATUS.DONE.label}</Typography.Text>
              </Choose.When>
              <Choose.When condition={actionStatus === STATUS.LATE.enum}>
                <Typography.Text style={STYLES.badgeLate}>{STATUS.LATE.label}</Typography.Text>
              </Choose.When>
              <Choose.Otherwise>
                <Typography.Text style={STYLES.badgeNotPlanned}>{STATUS.NOT_PLANNED.label}</Typography.Text>
              </Choose.Otherwise>
            </Choose>
          </Col>
        </Row>
        <br />
        <Row gutter={12}>
          <Col span={24}>
            <Typography.Text>{`${getMessage('description')}: ${actionSubject}`}</Typography.Text>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Typography.Text>{`${getMessage('template.date')}: `}</Typography.Text>
            <Typography.Text>{moment(actionPlannedDoneTime).locale('pt-br').utc().format('LL')}</Typography.Text>
          </Col>
        </Row>
        <br />
        <Row>
          <Col span={24}>
            <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }} style={{ marginBottom: '0px' }}>
              {actionNotes}
            </Typography.Paragraph>
          </Col>
        </Row>
        <br />
        <Row>
          <Col span={24}>
            <Icon type="team" theme="outlined" />
            <Typography.Text strong>{' ' + customerName}</Typography.Text>
          </Col>
          <Col span={24}>
            <Typography.Text>{customerTypeName}</Typography.Text>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Icon type="user" theme="outlined" />
            <Typography.Text strong>{' ' + memberName}</Typography.Text>
          </Col>
          <Col span={24}>
            <Typography.Text>{memberTypeName}</Typography.Text>
          </Col>
        </Row>

        <Divider />
        <Row type="flex" justify="end">
          <a onClick={() => history.push(`/actions/${actionTypeId}/${id}`)} style={STYLES.linkColor}>
            <Typography.Text strong>{'Visitas: '}</Typography.Text>
            <Icon type="edit" theme="filled" style={STYLES.editIcon} />
          </a>
        </Row>
      </>
    )
  }

  function renderTooltip(value = {}) {
    const { actionSubject, customerName } = value

    const hasAccess = validRule(RULES.ACTION)

    if (hasAccess) {
      return (
        <Popover placement="left" content={renderInnerTooltip(value)} trigger="click">
          <span style={STYLES.statusRealizado}>{actionSubject}</span>
          <br />
          <span style={STYLES.statusRealizado}>{customerName}</span>
        </Popover>
      )
    }

    return (
      <span style={STYLES.statusRealizado}>
        {actionSubject}
        <br />
        {customerName}
      </span>
    )
  }

  const renderFiltros = () => (
    <Formik initialValues={setFormDataKey} validateOnChange={false} validateOnBlur={false} onSubmit={() => ({})}>
      {({ errors, setFieldValue }) => {
        const setValueField = onChange(setFieldValue)
        return (
          <Form className="login-form form-customer" layout="vertical">
            <Row type="flex">
              <Col span={6}>
                {getMessage('actions.list.field.filterStakeholder')}
                <Input
                  style={{ marginTop: 5 }}
                  value={searchValues.customerName}
                  onChange={(e) => setFormDataKey('customerName', e.target.value)}
                />
              </Col>
              <Col span={6}>
                {getMessage('filterStatus')}
                <Select
                  value={searchValues.actionStatus}
                  style={{ width: '100%', marginTop: 5 }}
                  onChange={(status) => setFormDataKey('actionStatus', status)}
                >
                  {STATUS_ACTION.map((status) => (
                    <Option value={status.value} key={status.value}>
                      {status.description}
                    </Option>
                  ))}
                </Select>
              </Col>
              <CustomSelect
                field={'referencePeriodIds'}
                value={searchValues}
                rowSpan={6}
                values={referencePeriodList}
                errors={errors}
                setFieldValue={setValueField}
                showSearch
                multiple={true}
              />
              <Col span={6}>
                {getMessage('actions.list.field.filterDescription')}
                <Input
                  style={{ marginTop: 8 }}
                  value={searchValues.actionSubject}
                  onChange={(e) => setFormDataKey('actionSubject', e.target.value)}
                />
              </Col>
            </Row>

            <Row type="flex" style={{ marginBottom: 5, width: '102%' }}>
              <Col span={6}>
                {getMessage('customWalletMemberSelected')}
                <Input
                  style={{ marginTop: 8 }}
                  value={searchValues.memberName}
                  onChange={(e) => setFormDataKey('memberName', e.target.value)}
                />
              </Col>
            </Row>
            <div className="horizontal-line" style={{ margin: '20px 0px 20px' }} />
            <Row type="flex">
              <Col span={14} />
              <Col span={5}>
                <Button icon="search" className="bgPrimary" style={{ width: '100%' }} type="dashed" onClick={onSearch}>
                  {getMessage('search')}
                </Button>
              </Col>
              <Col span={5}>
                <Button
                  icon="close"
                  style={{
                    width: '100%',
                    borderColor: '#EE778F',
                    color: '#EE778F'
                  }}
                  type="ghost"
                  onClick={resetFilter}
                >
                  {getMessage('cleanFilters')}
                </Button>
              </Col>
            </Row>
          </Form>
        )
      }}
    </Formik>
  )

  return (
    <>
      {renderFiltros()}
      {renderTimeline(data)}
    </>
  )
}

export default ActionsTimeline
